import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../helpers/AuthContext';
import AutocompleteList from '../AutocompleteList';

export const CHANNEL_LOOKUP_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const LOOKUP_LIMIT = 10;

function channelTitle(channel) {
  return channel.basics?.title || channel.slug;
}

export default function ChannelLookupAutocompleteList({
  query,
  onSelect,
  excludedSlugs = [],
  id = 'channel-lookup-autocomplete',
}) {
  const { fetch, API_URL, channel: currentChannel } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const dismissedQueryRef = useRef('');

  const normalizedQuery = query.trim().toLowerCase();
  const excluded = useMemo(
    () => new Set([currentChannel?.slug, ...excludedSlugs].filter(Boolean)),
    [currentChannel?.slug, excludedSlugs],
  );
  const visibleResults = results.filter(result => !excluded.has(result.slug));
  const shouldLookup = normalizedQuery.length >= MIN_QUERY_LENGTH
    && normalizedQuery !== dismissedQueryRef.current;

  useEffect(() => {
    if (!shouldLookup) {
      setResults([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    dismissedQueryRef.current = '';
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError('');
    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          q: normalizedQuery,
          limit: String(LOOKUP_LIMIT),
        });
        const response = await fetch(
          `${API_URL}/v1/seller/channels/lookup?${params}`,
          {
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        );
        const json = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(json.error || 'Failed to look up channels');
        }
        if (!cancelled) setResults(json.data || []);
      } catch (lookupError) {
        if (!cancelled && lookupError.name !== 'AbortError') {
          setResults([]);
          setError(lookupError.message || 'Failed to look up channels');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, CHANNEL_LOOKUP_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
      controller.abort();
    };
  }, [API_URL, fetch, normalizedQuery, shouldLookup]);

  if (!shouldLookup) return null;

  function handleSelect(channel) {
    dismissedQueryRef.current = channel.slug;
    setResults([]);
    setError('');
    onSelect(channel);
  }

  return (
    <AutocompleteList
      id={id}
      items={visibleResults}
      getItemKey={channel => channel.uuid || channel.id || channel.slug}
      renderItem={channel => (
        <span className="fm-channel-suggestion">
          <strong>{channelTitle(channel)}</strong>
          <span>{channel.slug}</span>
        </span>
      )}
      onSelect={handleSelect}
      loading={loading}
      error={error}
      emptyMessage="No matching channels found."
      ariaLabel="Matching channels"
    />
  );
}
